import "./App.css";
import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Outlet,
  Navigate,
} from "react-router-dom";
import { useAuth } from "./components/AuthRouter/AuthContext";
import Layout from "./components/boilerPlat/layout";
import AdminWorkspace from "./v2/AdminWorkspace";

// Eager loading for critical components
import Login from "./components/Login";
import Register from "./components/Register";
import Dashboard from "./components/Dashboard";

// Lazy loading for routes
const UserProfile = lazy(() => import("./components/UserProfile"));
const Purchaseplan = lazy(() => import("./components/Purchaseplan"));
const NeedHelp = lazy(() => import("./components/NeedHelp"));
const CallLogs = lazy(() => import("./components/CallLogs"));
const ManageNo = lazy(() => import("./components/ManageNo"));
const BlockedNumbers = lazy(() => import("./lists/BlockedNumbers"));
const CreateCampaigns = lazy(() => import("./components/CreateCampaigns"));
const ManageCampaigns = lazy(() => import("./components/ManageCampaigns"));
const Publishers = lazy(() => import("./components/Publishers"));
const ManageBuyers = lazy(() => import("./components/Targets-pages/ManageBuyers"));
const CreateTargets = lazy(() => import("./components/Targets-pages/CreateTargets"));
const ManageTargets = lazy(() => import("./components/Targets-pages/ManageTargets"));
const ManageGroups = lazy(() => import("./components/Targets-pages/ManageGroups"));
const Activities = lazy(() => import("./components/Activities-pages/Activities"));
const Notification = lazy(() => import("./components/Activities-pages/Notifications"));
const UserActivities = lazy(() => import("./components/Activities-pages/UserActivities"));
const ManageUser = lazy(() => import("./components/Settings-pages/ManageUser"));
const Profile = lazy(() => import("./components/Settings-pages/Profile"));
const Sucess = lazy(() => import("./components/Sucess"));
const RefillHistory = lazy(() => import("./components/RefillHistory"));
const ReportList = lazy(() => import("./lists/ReportList"));
const PurchaseNumberList = lazy(() => import("./lists/PurchaseNumberList"));
const AddPurchaseNumber = lazy(() => import("./lists/AddPurchaseNumber"));
const Importpage = lazy(() => import("./components/Importpage"));
const CreateCustmer = lazy(() => import("./components/CreateCustmer"));
const CustomerReport = lazy(() => import("./lists/CustomerReport"));
const Sipdata = lazy(() => import("./lists/Sipdata"));
const CreateGatway = lazy(() => import("./components/CreateGatway"));
const Gatway = lazy(() => import("./components/Gatway"));
const EditGatway = lazy(() => import("./components/EditGatway"));
const Api = lazy(() => import("./components/Api"));
const EditCustomer = lazy(() => import("./components/EditCustomerPage"));
const CreateDID = lazy(() => import("./components/CreateDID"));
const EditDID = lazy(() => import("./components/EditDidPage"));
const IpWhiteList = lazy(() => import("./lists/IpWhiteList"));
const UserExtensions = lazy(() => import("./lists/UserExtensions"));
const DeleteUser = lazy(() => import("./components/Settings-pages/DeleteUser"));
const ChatBot = lazy(() => import("./components/ChatBot"));
const CampaignLiveCalls = lazy(() => import("./components/CampaignLiveCalls"));
const SipStatusTable = lazy(() => import("./components/SipStatusTable"));
const SendNotification = lazy(() => import("./components/models/SendNotification"));
const UserCountsTable = lazy(() => import("./lists/UserCountsTable"));
const Dialpad = lazy(() => import("./components/Dialpad"));
const CampaignsCalls = lazy(() => import("./components/CampaignsCalls"));
const ExtensioLiveCalls = lazy(() => import("./components/ExtensioLiveCalls"));
const TwilioNumber = lazy(() => import("./components/TwilioNumber"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-dashboard-primary"></div>
  </div>
);

// Wrapper component for layout
const RenderLayout = ({ children }) => <Layout>{children}</Layout>;

// Private Route component
const PrivateRoute = () => {
  const { loggedIn } = useAuth();
  return loggedIn ? <Outlet /> : <Navigate to="/" replace />;
};

// Route configuration object for better maintainability
const protectedRoutes = [
  { path: "/dashboard", component: Dashboard },
  { path: "/profile", component: UserProfile },
  { path: "/purchaseplan", component: Purchaseplan },
  { path: "/success", component: Sucess },
  { path: "/need-help", component: NeedHelp },
  { path: "/live-calls", component: ExtensioLiveCalls },
  { path: "/report-list", component: ReportList },
  { path: "/purchase-number-list", component: PurchaseNumberList },
  { path: "/add-purchase-number", component: AddPurchaseNumber },
  { path: "/block-number", component: BlockedNumbers },
  { path: "/refulil-histry", component: RefillHistory },
  { path: "/verify-ip", component: BlockedNumbers },
  { path: "/create-campaigns", component: CreateCampaigns },
  { path: "/manage-campaigns", component: ManageCampaigns },
  { path: "/publishers", component: Publishers },
  { path: "/manage-buyers", component: ManageBuyers },
  { path: "/create-targets", component: CreateTargets },
  { path: "/manage-targets", component: ManageTargets },
  { path: "/manage-groups", component: ManageGroups },
  { path: "/activities", component: Activities },
  { path: "/notifications", component: Notification },
  { path: "/user-activities", component: UserActivities },
  { path: "/manage-user", component: ManageUser },
  { path: "/Import-page", component: Importpage },
  { path: "/Create-Custmer", component: CreateCustmer },
  { path: "/edit-customer", component: EditCustomer },
  { path: "/Customer-Report", component: CustomerReport },
  { path: "/User-Counts", component: UserCountsTable },
  { path: "/sipdata", component: Sipdata },
  { path: "/create-gatway", component: CreateGatway },
  { path: "/create-DID", component: CreateDID },
  { path: "/editdid", component: EditDID },
  { path: "/gatway", component: Gatway },
  { path: "/editgatway", component: EditGatway },
  { path: "/api", component: Api },
  { path: "/ip-white-list", component: IpWhiteList },
  { path: "/delete-users", component: DeleteUser },
  { path: "/live-call", component: Dialpad },
  { path: "/send-notification", component: SendNotification },
  { path: "/extension-status", component: SipStatusTable },
  { path: "/campaign-live-call", component: CampaignLiveCalls },
  { path: "/extension-live-calls", component: ExtensioLiveCalls },
  { path: "/campaign-live-calls", component: CampaignsCalls },
  { path: "/twilio-number-fetch", component: TwilioNumber },
  { path: "/chat-boat", component: ChatBot },
];

// Special routes without layout
const specialRoutes = [
  { path: "/user-extensions/:userId", component: UserExtensions },
];

function App() {
  return (
    <div className="App bg-gray-50 dark:bg-gray-900 transition-colors duration-300 text-gray-800 dark:text-gray-100">
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/v2" element={<AdminWorkspace />} />
            <Route path="/v2-admin" element={<AdminWorkspace />} />

            {/* Protected routes */}
            <Route element={<PrivateRoute />}>
              {/* Routes with layout */}
              {protectedRoutes.map(({ path, component: Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={<RenderLayout><Component /></RenderLayout>}
                />
              ))}

              {/* Special routes without layout */}
              {specialRoutes.map(({ path, component: Component }) => (
                <Route key={path} path={path} element={<Component />} />
              ))}
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </div>
  );
}

export default App;
